import { QuoteStatus } from "enums/QuoteStatus";
import serialize from "form-serialize";
import FOB from "interfaces/FOB";
import QuoteExpiration from "interfaces/QuoteExpiration";
import UserIdAndName from "interfaces/UserIdAndName";
import * as moment from "moment";
import * as React from "react";
import DatePicker from "react-datepicker";
import MaskedInput from "react-text-mask";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import getJwtToken from "utils/getJwtToken";
import normalizePercentage from "utils/normalizePercentage";
import Step1Job from "./Step1Job";

interface Props {
    onSubmit: Function;
    onCancel: Function;
    isNewQuote: boolean;
    isDraft: boolean;
    salespeople: UserIdAndName[];
    defaultSalesTaxRate: number;
    fobs: FOB[];
    quoteExpirations: QuoteExpiration[];
    data: any;
}

interface State {
    quoteDate: moment.Moment;
    expirationDate: moment.Moment;
    quoteExpiration: number;
    isAdd: boolean;
    preparer: string | null;
    isNewJob: boolean;
    isMaster: boolean;
    isCashSale: boolean;
    isTaxable: boolean;
    showComments: boolean;
    showNotAwardedReason: boolean;
    showFOBOther: boolean;
}

interface Data {
    quoteName?: string;
    quoteDate: moment.Moment;
    expirationDate: moment.Moment;
    quoteExpiration: number;
    isMasterQuote: boolean;
    salesPerson: string;
    isCashSale: boolean;
    purchaseOrderNumber: string;
    quoteStatusId: number;
}

export default class Step1 extends React.Component<Props, State> {
    private step1JobRef: React.RefObject<Step1Job>;
    private formRef: React.RefObject<HTMLFormElement>;

    constructor(props: Props) {
        super(props);

        this.state = {
            quoteDate:
                this.props.data && this.props.data.date
                    ? moment(this.props.data.date)
                    : moment(),
            expirationDate:
                this.props.data && this.props.data.expirationDate
                    ? moment(this.props.data.expirationDate)
                    : moment().add(this.handleDefaultQuoteExpiration(), "days"),
            quoteExpiration: isNaN(
                moment
                    .duration(
                        moment(this.props.data.expirationDate).diff(
                            moment(this.props.data.date)
                        )
                    )
                    .asDays()
            )
                ? this.handleDefaultQuoteExpiration()
                : moment
                    .duration(
                        moment(this.props.data.expirationDate).diff(
                            moment(this.props.data.date)
                        )
                    )
                    .asDays(),
            preparer: getJwtToken()!.name, // Will always be the name of the person adding/editing the quote
            isAdd: !(this.props.data && this.props.data.date),
            isNewJob: false,
            isMaster: this.props.data.isMaster,
            isCashSale: this.props.data.isCashSale,
            isTaxable: this.props.data.salesTaxRate != null,
            showComments: this.props.data.quoteStatusId === 5,
            showNotAwardedReason: this.props.data.quoteStatusId === 6,
            showFOBOther: this.props.data.fobId == null,
        };

        this.step1JobRef = React.createRef();
        this.formRef = React.createRef();

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleSubmitAsDraft = this.handleSubmitAsDraft.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
        this.handleInputChanged = this.handleInputChanged.bind(this);
        this.handleBooleanInputChanged = this.handleBooleanInputChanged.bind(this);
        this.quoteDateChanged = this.quoteDateChanged.bind(this);
        this.expirationDateChanged = this.expirationDateChanged.bind(this);
        this.handleQuoteStatusIdChanged =
            this.handleQuoteStatusIdChanged.bind(this);
        this.handleFOBIdChanged = this.handleFOBIdChanged.bind(this);
        this.handleQuoteExpirationIdChanged =
            this.handleQuoteExpirationIdChanged.bind(this);
        this.handleDefaultQuoteExpiration =
            this.handleDefaultQuoteExpiration.bind(this);
    }
    default() {
        throw new Error("Method not implemented.");
    }

    public render() {
        const percentageMask = createNumberMask({
            prefix: "",
            suffix: "%",
            allowDecimal: true,
            decimalLimit: 4,
        });

        return (
            <div className="box-wrap">
                <form onSubmit={this.handleSubmit} ref={this.formRef}>
                    <h2 className="card__title">
                        {this.props.isNewQuote ? <>Add New Quote</> : <>Edit Quote</>}
                    </h2>
                    <div className="box">
                        <Step1Job data={this.props.data} ref={this.step1JobRef} />
                        <div className="row">
                            <div className="field-group col-sm-6 d-flex align-items-center datepicker-full-width">
                                <span className="field-label mr-2">Quote Date</span>
                                <DatePicker
                                    name="date"
                                    selected={this.state.quoteDate}
                                    onChange={this.quoteDateChanged}
                                    dateFormat="LL"
                                    className="form-control"
                                    popperPlacement="bottom"
                                    popperModifiers={{
                                        flip: {
                                            behavior: ["bottom"],
                                        },
                                        preventOverflow: {
                                            enabled: false,
                                        },
                                        hide: {
                                            enabled: false,
                                        },
                                    }}
                                />
                            </div>
                            <div className="field-group col-sm-6">
                                <span className="field-label">Quote Expiration</span>
                                <select
                                    name="quoteExpiration"
                                    className="form-control selectbox"
                                    style={{ paddingTop: "0", paddingBottom: "0" }}
                                    onChange={this.handleQuoteExpirationIdChanged}
                                    defaultValue={
                                        this.state.isAdd
                                            ? `${this.handleDefaultQuoteExpiration()}`
                                            : `${this.state.quoteExpiration}`
                                    }
                                >
                                    {this.props.quoteExpirations.map((item, index) => (
                                        <option value={item.expirationDays}>
                                            {item.expirationDays === 5
                                                ? "Five Days"
                                                : item.expirationDays === 10
                                                    ? "Ten Days"
                                                    : item.expirationDays === 15
                                                        ? "Fifteen Days"
                                                        : "Thirty Days"}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="field-group col-sm-6 d-flex align-items-center datepicker-full-width">
                                <span className="field-label mr-2">Expiration Date</span>
                                <DatePicker
                                    name="expirationDate"
                                    selected={this.state.expirationDate}
                                    onChange={this.expirationDateChanged}
                                    readOnly={true}
                                    dateFormat="LL"
                                    className="form-control"
                                    popperPlacement="bottom"
                                    popperModifiers={{
                                        flip: {
                                            behavior: ["bottom"],
                                        },
                                        preventOverflow: {
                                            enabled: false,
                                        },
                                        hide: {
                                            enabled: false,
                                        },
                                    }}
                                />
                            </div>
                            <div className="field-group col-sm-12 mt-3 mb-3">
                                <span className="field-label mt-0">Master Quote</span>
                                <p className="custom-checkbox-wrap">
                                    <input
                                        className="custom-checkbox"
                                        id="master-quote-yes"
                                        name="isMaster"
                                        type="radio"
                                        value="true"
                                        checked={this.state.isMaster}
                                        onChange={this.handleBooleanInputChanged}
                                    />
                                    <label htmlFor="master-quote-yes">Yes</label>
                                </p>
                                <p className="custom-checkbox-wrap">
                                    <input
                                        className="custom-checkbox"
                                        id="master-quote-no"
                                        name="isMaster"
                                        type="radio"
                                        value="false"
                                        checked={!this.state.isMaster}
                                        onChange={this.handleBooleanInputChanged}
                                    />
                                    <label htmlFor="master-quote-no">No</label>
                                </p>
                            </div>
                            <div className="field-group col-sm-6">
                                <span className="field-label">Salesperson</span>
                                <select
                                    name="salespersonUserId"
                                    className="form-control selectbox"
                                    style={{ paddingTop: "0", paddingBottom: "0" }}
                                    defaultValue={this.props.data.salespersonUserId}
                                >
                                    <option value="">Select a salesperson</option>
                                    {this.props.salespeople.map((value, index) => (
                                        <option value={value.id}>{value.fullName}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="field-group col-sm-6">
                                <span className="field-label">Preparer</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={this.state.preparer || ""}
                                    disabled
                                />
                            </div>
                            <div className="field-group col-sm-6 mt-3 mb-3">
                                <span className="field-label mt-0">COD</span>
                                <p className="custom-checkbox-wrap">
                                    <input
                                        className="custom-checkbox"
                                        id="cod-yes"
                                        name="isCashSale"
                                        value="true"
                                        type="radio"
                                        checked={this.state.isCashSale}
                                        onChange={this.handleBooleanInputChanged}
                                    />
                                    <label htmlFor="cod-yes">Yes</label>
                                </p>
                                <p className="custom-checkbox-wrap">
                                    <input
                                        className="custom-checkbox"
                                        id="cod-no"
                                        name="isCashSale"
                                        value="false"
                                        type="radio"
                                        checked={!this.state.isCashSale}
                                        onChange={this.handleBooleanInputChanged}
                                    />
                                    <label htmlFor="cod-no">No</label>
                                </p>
                            </div>
                            <div className="field-group col-sm-6">
                                <span className="field-label">PO #</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="purchaseOrderNumber"
                                    defaultValue={this.props.data.purchaseOrderNumber}
                                />
                            </div>
                            <div className="field-group col-sm-6 mt-3 mb-3">
                                <span className="field-label mt-0">Taxable</span>
                                <p className="custom-checkbox-wrap">
                                    <input
                                        className="custom-checkbox"
                                        id="taxable-yes"
                                        name="isTaxable"
                                        value="true"
                                        type="radio"
                                        checked={this.state.isTaxable}
                                        onChange={this.handleBooleanInputChanged}
                                    />
                                    <label htmlFor="taxable-yes">Yes</label>
                                </p>
                                <p className="custom-checkbox-wrap">
                                    <input
                                        className="custom-checkbox"
                                        id="taxable-no"
                                        name="isTaxable"
                                        value="false"
                                        type="radio"
                                        checked={!this.state.isTaxable}
                                        onChange={this.handleBooleanInputChanged}
                                    />
                                    <label htmlFor="taxable-no">No</label>
                                </p>
                            </div>
                            <div className="field-group col-sm-6">
                                {this.state.isTaxable && (
                                    <>
                                        <span className="field-label">Sales Tax Rate</span>
                                        <MaskedInput
                                            mask={percentageMask}
                                            name="salesTaxRate"
                                            className="form-control"
                                            value={(
                                                (this.props.data.salesTaxRate != null
                                                    ? this.props.data.salesTaxRate!
                                                    : this.props.defaultSalesTaxRate) * 100
                                            ).toString()}
                                        />
                                    </>
                                )}
                            </div>
                            <div className="field-group col-sm-6">
                                <span className="field-label">FOB</span>
                                <select
                                    name="fobId"
                                    className="form-control selectbox"
                                    style={{ paddingTop: "0", paddingBottom: "0" }}
                                    onChange={this.handleFOBIdChanged}
                                    defaultValue={this.props.data.fobId || ""}
                                >
                                    {this.props.fobs.map((value, index) => (
                                        <option value={value.id}>{value.name}</option>
                                    ))}
                                    <option value="">Other (specify)</option>
                                </select>
                            </div>
                            <div className="field-group col-sm-6">
                                {this.state.showFOBOther && (
                                    <>
                                        <span className="field-label">FOB (Other)</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="fobOther"
                                            defaultValue={this.props.data.fobOther}
                                            required
                                            maxLength={50}
                                        />
                                    </>
                                )}
                            </div>
                            <div className="field-group col-sm-6">
                                <span className="field-label">Status</span>
                                <select
                                    name="quoteStatusId"
                                    className="form-control selectbox"
                                    style={{ paddingTop: "0", paddingBottom: "0" }}
                                    onChange={this.handleQuoteStatusIdChanged}
                                    defaultValue={this.props.data.quoteStatusId}
                                >
                                    <option value={QuoteStatus.Pending}>Pending</option>
                                    <option value={QuoteStatus.LowAppBidder}>
                                        Low Apparent Bidder
                                    </option>
                                    <option value={QuoteStatus.ReQuotePending}>
                                        Re-Quote Pending
                                    </option>
                                    {BQS && (
                                        <option value={QuoteStatus.BrentAward}>Brent Award</option>
                                    )}
                                    {BQS && (
                                        <option value={QuoteStatus.BrentPartialAward}>
                                            Brent Partial Award
                                        </option>
                                    )}
                                    {BQS && (
                                        <option value={QuoteStatus.BrentLostJob}>
                                            Brent Lost Project
                                        </option>
                                    )}
                                    {CQS && (
                                        <option value={QuoteStatus.BrentAward}>
                                            Caterina Award
                                        </option>
                                    )}
                                    {CQS && (
                                        <option value={QuoteStatus.BrentPartialAward}>
                                            Caterina Partial Award
                                        </option>
                                    )}
                                    {CQS && (
                                        <option value={QuoteStatus.BrentLostJob}>
                                            Caterina Lost Project
                                        </option>
                                    )}
                                    <option value={QuoteStatus.CustomerDidNotGetJob}>
                                        Customer Did Not Get Project
                                    </option>
                                    <option value={QuoteStatus.Cancelled}>Cancelled</option>
                                    <option value={QuoteStatus.Void}>Void</option>
                                    <option value={QuoteStatus.Closed}>Closed</option>
                                </select>
                            </div>

                            {this.state.showComments && (
                                <div className="field-group col-sm-12">
                                    <div className="mb-2">Comments</div>
                                    <textarea
                                        id="description"
                                        name="comments"
                                        rows={6}
                                        className="form-control"
                                    ></textarea>
                                </div>
                            )}
                            {this.state.showNotAwardedReason && (
                                <div className="field-group col-sm-12">
                                    <div className="mb-2">Reason for not being awarded</div>
                                    <textarea
                                        id="description"
                                        name="notAwardedReason"
                                        rows={6}
                                        className="form-control"
                                    ></textarea>
                                </div>
                            )}
                        </div>
                    </div>
                    <div
                        className="button-actions text-center"
                        style={{ marginBottom: "150px" }}
                    >
                        <button type="submit" className="btn btn-primary">
                            Next
                        </button>
                        {(this.props.isNewQuote || this.props.isDraft) && (
                            <button
                                className="btn btn-primary"
                                onClick={this.handleSubmitAsDraft}
                            >
                                Save as Draft
                            </button>
                        )}
                        <button className="btn-link" onClick={this.handleCancel}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    public componentDidMount() {
        document.title = "Add New Quote - " + SITE_NAME;
    }

    private handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        let data = serialize(e.currentTarget, { hash: true }) as any;

        // Normalize percentage
        if (data.salesTaxRate) {
            data.salesTaxRate = normalizePercentage(data.salesTaxRate);
        }

        this.step1JobRef.current!.handleSubmit(data, (data: any) =>
            this.props.onSubmit(data)
        );
    }

    private handleSubmitAsDraft(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        let data = serialize(this.formRef.current!, { hash: true }) as any;
        data.saveAsDraft = true;

        // Normalize percentage
        if (data.salesTaxRate) {
            data.salesTaxRate = normalizePercentage(data.salesTaxRate);
        }

        this.step1JobRef.current!.handleSubmit(data, (data: any) =>
            this.props.onSubmit(data)
        );
    }

    private handleCancel(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        this.props.onCancel();
    }

    private handleInputChanged(event: React.SyntheticEvent<HTMLInputElement>) {
        this.setState({
            [event.currentTarget.name]: event.currentTarget.value,
        } as any);
    }

    private handleBooleanInputChanged(
        event: React.SyntheticEvent<HTMLInputElement>
    ) {
        this.setState({
            [event.currentTarget.name]: event.currentTarget.value === "true",
        } as any);
    }

    private quoteDateChanged(date: moment.Moment) {
        this.setState({
            quoteDate: date,
            expirationDate: moment(date).add(this.state.quoteExpiration, "days"),
        });
    }

    private expirationDateChanged(date: moment.Moment) {
        this.setState({
            //expirationDate: date,
            expirationDate: this.state.expirationDate,
        });
    }

    private handleQuoteStatusIdChanged(e: React.FormEvent<HTMLSelectElement>) {
        let value = parseInt(e.currentTarget.value, 10) || 0;
        this.setState({
            showComments: value === 5,
            showNotAwardedReason: value === 6,
        });
    }

    private handleFOBIdChanged(e: React.FormEvent<HTMLSelectElement>) {
        this.setState({
            showFOBOther: e.currentTarget.value === "",
        });
    }

    private handleQuoteExpirationIdChanged(
        e: React.FormEvent<HTMLSelectElement>
    ) {
        this.setState({
            quoteExpiration: Number(e.currentTarget.value),
            expirationDate: moment(this.state.quoteDate).add(
                Number(e.currentTarget.value),
                "days"
            ),
        });
    }

    private handleDefaultQuoteExpiration() {
        let data = this.props.quoteExpirations;
        let defaultquoteExpiration = data.filter((item) => item.isDefault)[0]
            .expirationDays;

        return defaultquoteExpiration;
    }
}



// WEBPACK FOOTER //
// ./ClientApp/components/quotes/Step1.tsx